import React from 'react';
import { Image } from 'antd';
import { UserOutlined } from '@ant-design/icons';

/**
 * Read-only "who sold us this gold" block for the bill document.
 *
 * Deliberately not the CreateBill CustomerDetails component: that one is a form
 * control (click-to-upload photo, live state) and rendering it on a finished
 * document invites edits to a record that is meant to be frozen.
 */

// Matches the printed PDF, which never shows a full Aadhaar number.
const maskAadhaar = (v) => {
  const s = String(v || '').replace(/\s+/g, '');
  if (!s || s === '-') return null;
  return s.length > 4 ? `XXXX XXXX ${s.slice(-4)}` : s;
};

const BillPartyDetails = ({ customer = {}, images = [] }) => {
  const photo =
    customer.photo?.url ||
    customer.photo?.thumbnailUrl ||
    (typeof customer.photo === 'string' ? customer.photo : null) ||
    customer.kycData?.photo ||
    null;

  const initials = (customer.name || '')
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const fields = [
    ['Name', customer.name],
    ['Phone', customer.phone],
    ['Aadhaar', maskAadhaar(customer.aadhaar)],
    ['PAN', customer.pan],
    ['Email', customer.email],
    ['Address', customer.address],
  ].filter(([, v]) => v);

  const goldImages = Array.isArray(images) ? images.filter((i) => i?.url || i?.thumbnailUrl) : [];

  return (
    <div className="bill-parties">
      {/* ── Seller ── */}
      <section className="bill-panel">
        <h4 className="bill-panel__title">Seller / Customer Details</h4>

        <div className="bill-party">
          <div className="bill-party__photo">
            {photo ? (
              <Image 
                src={photo} 
                alt={customer.name || 'Customer'} 
                wrapperClassName="bill-party__img-wrapper"
                className="bill-party__img" 
              />
            ) : (
              <div className="bill-party__placeholder">
                {initials || <UserOutlined />}
              </div>
            )}
          </div>

          <table className="bill-party__fields">
            <tbody>
              {fields.length > 0 ? (
                fields.map(([label, value]) => (
                  <tr key={label}>
                    <td className="bill-party__label">{label}</td>
                    <td className="bill-party__value">{value}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="bill-party__value bill-muted">No customer details recorded.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Goods photographed at intake ── */}
      {goldImages.length > 0 && (
        <section className="bill-panel">
          <h4 className="bill-panel__title">Gold Photographs</h4>
          <div className="bill-goldshots">
            <Image.PreviewGroup>
              {goldImages.map((img, idx) => (
                <Image
                  key={img.fileId || img.url || idx}
                  src={img.thumbnailUrl || img.url}
                  preview={{ src: img.url || img.thumbnailUrl }}
                  alt={img.name || `Gold item ${idx + 1}`}
                  wrapperClassName="bill-goldshot-wrapper"
                  className="bill-goldshot"
                />
              ))}
            </Image.PreviewGroup>
          </div>
        </section>
      )}
    </div>
  );
};

export default BillPartyDetails;
